import { useMemo } from 'react';
import classNames from './MoveStudentsPopup.module.scss';
import { useAdminTranslation } from '@/admin/hooks/useAdminTranslation';
import { StudentReportItem } from '@/school-management/types/response';
import {
  MoveClassDestination,
  BulkOperationResponseData,
  MoveStudentsBulkOperationItem,
  BulkOperationState,
} from '@/school-management/types/services/bulk';
import Button from '@/components/school-management/Button/Button';
import StudentsTable, {
  convertStudentReportItemToStudentsTableData,
} from './StudentsTablePartialSuccesses';
import Download from './Download';

interface PartialSuccessesProps {
  students: StudentReportItem[];
  classDestination: MoveClassDestination;
  moveSource: 'ChooseDestination' | 'DifferentGradeAll' | 'DifferentGradeSame';
  bulkOperation?: BulkOperationResponseData;
  onClose: () => void;
}

const PartialSuccesses = ({
  students,
  classDestination,
  moveSource,
  bulkOperation,
  onClose,
}: PartialSuccessesProps) => {
  const { t } = useAdminTranslation();

  const studentsTableData = useMemo(() => {
    let studentsToProcess = students || [];

    if (moveSource === 'DifferentGradeSame') {
      studentsToProcess = studentsToProcess.filter(s => s.gradeId === classDestination.gradeId);
    }

    const bulkStudentsMapper = (
      (bulkOperation?.results || []) as MoveStudentsBulkOperationItem[]
    ).reduce(
      (acc, s) => {
        if (s.studentId) {
          acc[s.studentId] = s;
        }

        return acc;
      },
      {} as {
        [studentId: string]: MoveStudentsBulkOperationItem;
      }
    );

    return studentsToProcess.map(s => {
      const isSuccess =
        s.userId in bulkStudentsMapper ? bulkStudentsMapper[s.userId].success : true;
      return convertStudentReportItemToStudentsTableData(s, isSuccess, t);
    });
  }, [students, classDestination, moveSource, bulkOperation, t]);

  return (
    <>
      <div>
        <div className={classNames.title} style={{ fontSize: '26px' }}>
          {t('bulkActions.common.partialSuccessTitle')}
        </div>
        <div className={classNames.description}>
          {t('bulkActions.moveStudents.partialSuccesses.description')}
        </div>
      </div>
      <StudentsTable students={studentsTableData} />
      <Download
        students={students}
        classDestination={classDestination}
        moveSource={moveSource}
        bulkOperation={bulkOperation}
        bulkOperationState={BulkOperationState.PartialSuccess}
      />
      <div className={classNames.footer}>
        <Button className={classNames.button} onClick={onClose}>
          {t('ok')}
        </Button>
      </div>
    </>
  );
};

export default PartialSuccesses;
